import { useState, useCallback, useEffect, JSX } from "react"
import { ExternalLink, FilePlusCorner, FileText, Loader2, Plus, Upload, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Survey, addReportToSurvey, addReportFileToSurvey } from "@/services/surveysListService"
import { useAuth } from "@/hooks/useAuth"
import { useFileUpload } from "@/hooks/use-file-upload.ts"
import { addReportToBiblio, addFilesToSource, getFilesForSource, GoogleDriveFile, NewReportRequest } from "@/services/biblioService"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx"


/**
 * Props for the AddReportDialog component.
 *
 * @property onReportCreated - Callback invoked after the report is successfully
 *   created and linked to the survey. The parent typically re-fetches surveys.
 * @property survey - The survey to which the report will be linked.
 */
interface AddReportDialogProps {
  onReportCreated: () => void
  survey: Survey
}


/**
 * Props for the SourceFileCard sub-component.
 *
 * @property file - The Google Drive file metadata to display.
 * @property isSelected - Whether this file is currently selected as the report file.
 * @property onSelect - Callback fired when the user clicks to select this file.
 */
interface SourceFileCardProps {
  file: GoogleDriveFile
  isSelected: boolean
  onSelect: () => void
}

/**
 * Renders a selectable card for a Google Drive file with a thumbnail preview
 * and an external link to open the file in a new tab. Used within the "Select
 * Existing File" section of the AddReportDialog.
 *
 * Shows the file thumbnail if available (falls back to a file icon), the file
 * name, and an external-link button to open it in Google Drive. Clicking the
 * card body selects it; clicking the link opens the file without selecting.
 *
 * @param props - Component props.
 * @param props.file - The Google Drive file metadata.
 * @param props.isSelected - Whether this file is currently selected.
 * @param props.onSelect - Handler to call when selected.
 * @returns The rendered source file card element.
 */
function SourceFileCard({ file, isSelected, onSelect }: SourceFileCardProps): JSX.Element {
  const [thumbnailError, setThumbnailError] = useState<boolean>(false)
  const showThumbnail = file.thumbnailLink && !thumbnailError

  return (
    <div
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect()
        }
      }}
      className={`relative flex flex-col items-center gap-1.5 p-2 rounded-md border cursor-pointer transition-all w-[140px] ${
        isSelected
          ? "border-primary ring-2 ring-primary/30 bg-primary/5"
          : "border-border hover:border-primary/40 hover:bg-muted/50"
      }`}
    >
      {/* Thumbnail or fallback icon */}
      <div className="flex items-center justify-center w-full h-[80px] overflow-hidden rounded">
        {showThumbnail ? (
          <img
            src={file.thumbnailLink}
            alt={file.name || "File thumbnail"}
            onError={() => setThumbnailError(true)}
            className="max-w-full max-h-[80px] object-contain"
          />
        ) : (
          <FileText className="size-10 text-muted-foreground" />
        )}
      </div>

      {/* File name + external link */}
      <div className="flex items-center gap-1 w-full min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs truncate flex-1 text-center">
              {file.name || "Unnamed file"}
            </span>
          </TooltipTrigger>
          <TooltipContent>{file.name || "Unnamed file"}</TooltipContent>
        </Tooltip>

        {file.webViewLink && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={file.webViewLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
                aria-label={`Open "${file.name}" in new tab`}
              >
                <ExternalLink className="size-3" />
              </a>
            </TooltipTrigger>
            <TooltipContent>Open in new tab</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <span className="absolute top-1 right-1 size-2 rounded-full bg-primary" aria-hidden="true" />
      )}
    </div>
  )
}


/**
 * Dialog component for adding a new report to a survey.
 *
 * Collects a report file (required) and metadata (title required, plus optional
 * fields like authors, publication year, etc.). On submission it creates a biblio
 * record via addReportToBiblio, then links the resulting sourceId and Google Drive
 * file to the survey via addReportToSurvey.
 *
 * When the user chooses "Add Existing Source" and enters a source ID, the dialog
 * fetches any files already attached to that source and allows the user to either
 * upload a new file or select one of the existing files as the report file.
 *
 * @param props - Component props.
 * @param props.onReportCreated - Callback fired after successful report creation.
 * @param props.survey - The survey this report will be attached to.
 * @returns The rendered dialog trigger and content.
 */
export default function AddReportDialog({ onReportCreated, survey }: AddReportDialogProps): JSX.Element {
  const { getAccessToken, user } = useAuth()

  const [{ files }, { openFileDialog, removeFile, getInputProps, clearFiles }] = useFileUpload({
    accept: "*",
    multiple: false,
  })

  // Dialog open state
  const [open, setOpen] = useState<boolean>(false)

  // Form state — required fields
  const [title, setTitle] = useState<string>("")

  // Form state — optional fields
  const [publicationYear, setPublicationYear] = useState<string>("")
  const [authors, setAuthors] = useState<string>("")
  const [institutions, setInstitutions] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [reportNumber, setReportNumber] = useState<string>("")
  const [pages, setPages] = useState<string>("")

  // Biblio source mode (used when survey has no reportBiblioId): null, "new", or "existing"
  const [biblioMode, setBiblioMode] = useState<"new" | "existing" | null>(null)

  // Form state — existing source ID (when biblioMode === "existing")
  const [existingSourceId, setExistingSourceId] = useState<string>("")

  // File mode for the "existing source" flow: whether to upload a new file or select an existing one
  const [fileMode, setFileMode] = useState<"upload" | "existing" | null>(null)

  // Files already attached to the existing biblio source (fetched from API)
  const [sourceFiles, setSourceFiles] = useState<GoogleDriveFile[]>([])
  const [sourceFilesLoading, setSourceFilesLoading] = useState<boolean>(false)
  const [sourceFilesError, setSourceFilesError] = useState<string | null>(null)

  // The Google Drive file ID selected from the existing source files list
  const [selectedExistingFileId, setSelectedExistingFileId] = useState<string | null>(null)

  // Submission state
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)


  /**
   * Fetch files for the current existingSourceId from the biblio API.
   * Called when the user clicks "Load Files" or when the sourceId input loses focus.
   *
   * @returns void
   */
  const fetchSourceFiles = useCallback(async (): Promise<void> => {
    const trimmedId = existingSourceId.trim()
    if (!trimmedId) {
      setSourceFiles([])
      setSourceFilesError(null)
      return
    }

    const numericId = parseInt(trimmedId, 10)
    if (isNaN(numericId)) {
      setSourceFilesError("Source ID must be a number.")
      setSourceFiles([])
      return
    }

    setSourceFilesLoading(true)
    setSourceFilesError(null)
    setSelectedExistingFileId(null)

    try {
      const token = await getAccessToken()
      const response = await getFilesForSource(token, numericId)
      setSourceFiles(response.files)
      if (response.files.length === 0) {
        setSourceFilesError("No files found for this source.")
      }
    } catch (err) {
      setSourceFilesError(err instanceof Error ? err.message : "Failed to fetch source files.")
      setSourceFiles([])
    } finally {
      setSourceFilesLoading(false)
    }
  }, [existingSourceId, getAccessToken])

  /**
   * Automatically fetch source files when the existing source ID changes
   * (debounced — triggers after user stops typing for 800ms).
   */
  useEffect(() => {
    // Only fetch when in "existing" biblio mode
    if (biblioMode !== "existing") return

    const trimmedId = existingSourceId.trim()
    if (!trimmedId) {
      setSourceFiles([])
      setSourceFilesError(null)
      return
    }

    // Debounce: wait 800ms after user stops typing before fetching
    const timeoutId = setTimeout(() => {
      fetchSourceFiles()
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [existingSourceId, biblioMode, fetchSourceFiles])


  /**
   * Reset all form fields to their initial empty state.
   */
  const resetForm = useCallback((): void => {
    setTitle("")
    setPublicationYear("")
    setAuthors("")
    setInstitutions("")
    setCity("")
    setReportNumber("")
    setPages("")
    setBiblioMode(null)
    setExistingSourceId("")
    setFileMode(null)
    setSourceFiles([])
    setSourceFilesError(null)
    setSelectedExistingFileId(null)
    setSubmitError(null)
  }, [])

  /**
   * Handle form submission.
   *
   * If the survey already has a reportBiblioId, uploads the file(s) to the
   * existing biblio source and updates the survey's reportBiblioFile.
   *
   * If the survey has no reportBiblioId, the user must choose:
   * - "new": creates a new biblio source via addReportToBiblio, then links it.
   * - "existing": user can either upload a new file (calls addFilesToSource) or
   *   select an existing file from the source. Then links the source to the
   *   survey via addReportToSurvey.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    setSubmitError(null)

    setSubmitting(true)
    try {
      const token = await getAccessToken()

      if (survey.reportBiblioId) {
        // Survey already has a linked biblio source — add files to it
        if (files.length === 0) {
          setSubmitError("At least one file is required.")
          setSubmitting(false)
          return
        }

        const addFilesResponse = await addFilesToSource(
          token,
          survey.reportBiblioId,
          files
        )

        const driveFileId: string | undefined = addFilesResponse.driveFileIds?.[0]
        const sourceFileId: string | undefined = driveFileId
          ? `https://drive.google.com/file/d/${driveFileId}/view`
          : undefined

        if (sourceFileId) {
          await addReportFileToSurvey(token, {
            mapSurveyId: survey.mapSurveyId,
            sourceFileId,
          })
        }
      } else if (biblioMode === "new") {
        // Create a new biblio source
        if (!title.trim()) {
          setSubmitError("Title is required.")
          setSubmitting(false)
          return
        }
        if (files.length === 0) {
          setSubmitError("A report file is required when creating a new source.")
          setSubmitting(false)
          return
        }

        const authorsList: string[] = authors
          .split(";")
          .map(author => author.trim())
          .filter(author => author.length > 0)

        const institutionsList: string[] = institutions
          .split(",")
          .map(institution => institution.trim())
          .filter(institution => institution.length > 0)

        const newReportRequest: NewReportRequest = {
          title: title.trim(),
          files,
          mapSurveyId: survey.mapSurveyId,
          surveyType: survey.surveyType,
          publicationYear: publicationYear.trim(),
          authors: authorsList,
          institutions: institutionsList,
          reportNumber: reportNumber.trim(),
          pages: pages.trim(),
          city: city.trim(),
          createdBy: user?.email ?? "unknown",
        }

        const reportResponse = await addReportToBiblio(token, newReportRequest)

        const sourceFileId: string | undefined = reportResponse.driveFileId
          ? `https://drive.google.com/file/d/${reportResponse.driveFileId}/view`
          : undefined

        await addReportToSurvey(token, {
          sourceId: String(reportResponse.sourceId),
          sourceFileId,
          mapSurveyId: survey.mapSurveyId,
          surveyType: survey.surveyType,
        })
      } else if (biblioMode === "existing") {
        // Link an existing biblio source
        if (!existingSourceId.trim()) {
          setSubmitError("Source ID is required.")
          setSubmitting(false)
          return
        }

        let sourceFileId: string | undefined

        if (fileMode === "upload" && files.length > 0) {
          // User chose to upload a new file — add it to the existing source
          const addFilesResponse = await addFilesToSource(
            token,
            existingSourceId.trim(),
            files
          )

          const driveFileId: string | undefined = addFilesResponse.driveFileIds?.[0]
          sourceFileId = driveFileId
            ? `https://drive.google.com/file/d/${driveFileId}/view`
            : undefined
        } else if (fileMode === "existing" && selectedExistingFileId) {
          // User selected an existing file from the source's attached files
          sourceFileId = `https://drive.google.com/file/d/${selectedExistingFileId}/view`
        }
        // If fileMode is null or no file was selected/uploaded, sourceFileId stays undefined

        await addReportToSurvey(token, {
          sourceId: existingSourceId.trim(),
          sourceFileId,
          mapSurveyId: survey.mapSurveyId,
          surveyType: survey.surveyType,
        })
      } else {
        setSubmitError("Please select whether to create a new source or add an existing one.")
        setSubmitting(false)
        return
      }

      resetForm()
      clearFiles()
      setOpen(false)
      onReportCreated()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to add report")
    } finally {
      setSubmitting(false)
    }
  }, [
    title,
    files,
    authors,
    institutions,
    publicationYear,
    reportNumber,
    pages,
    city,
    survey,
    user,
    biblioMode,
    existingSourceId,
    fileMode,
    selectedExistingFileId,
    getAccessToken,
    resetForm,
    clearFiles,
    onReportCreated,
  ])

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        // Prevent closing the dialog while a submission is in progress
        if (!isOpen && submitting) return
        setOpen(isOpen)
        if (!isOpen) {
          resetForm()
          clearFiles()
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon" className="size-7">
              <Plus className="size-3.5" />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Add report file to survey</TooltipContent>
      </Tooltip>{" "}
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
        showCloseButton={!submitting}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault() }}
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault() }}
      >
        <DialogHeader>
          <DialogTitle>Add Report to Survey</DialogTitle>
          <DialogDescription>
            {survey.reportBiblioId
              ? `Upload a file to the existing biblio source ${survey.reportBiblioId} (https://apps.malariaatlas.org/map-biblio/sources/${survey.reportBiblioId}) linked to survey ${survey.mapSurveyId}.`
              : `Upload a report file and provide metadata. This will create a biblio record and link it to survey ${survey.mapSurveyId}. Required fields are marked with *.`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {survey.reportBiblioId ? (
            <>
              <div className="grid gap-1.5">
                <label htmlFor="report-file-upload" className="text-sm font-medium">
                  Report File *
                </label>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openFileDialog}
                    className="gap-2"
                  >
                    <Upload className="size-4" />
                    Choose File
                  </Button>
                  <input {...getInputProps()} className="sr-only" />
                  {files.length > 0 && (
                    <ul className="space-y-1">
                      {files.map(file => (
                        <li key={file.id} className="flex items-center gap-2 text-sm">
                          <span className={"wrap-break-word max-w-[435px]"}>{file.file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="size-5 p-0"
                            onClick={() => removeFile(file.id)}
                            aria-label={`Remove ${file.file.name}`}
                          >
                            <X className="size-3" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Biblio Source section — choose new or existing */}
              <div className="pt-2">
                <p className="text-sm font-medium text-muted-foreground mb-3">
                  Create new report biblio source to link to survey (or link existing source)
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={biblioMode === "new" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBiblioMode("new")}
                  >
                    <Plus className="size-4 mr-0.5" />
                    Create New Source
                  </Button>
                  <Button
                    type="button"
                    variant={biblioMode === "existing" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBiblioMode("existing")}
                  >
                    <FilePlusCorner className="size-4 mr-0.5" />
                    Add Existing Source
                  </Button>
                </div>

                {/* Existing source — enter sourceId + file options */}
                {biblioMode === "existing" && (
                  <div className="grid gap-4 mt-3">
                    <div className="grid gap-1.5">
                      <label htmlFor="existing-source-id" className="text-sm font-medium">
                        Source ID *
                      </label>
                      <Input
                        id="existing-source-id"
                        placeholder="e.g. 1001461050"
                        value={existingSourceId}
                        onChange={e => setExistingSourceId(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Enter the numeric biblio source ID to link to this survey.
                      </p>
                    </div>

                    {/* Report File — choose between upload or select existing */}
                    <div className="grid gap-1.5">
                      <label className="text-sm font-medium">
                        Report File
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Upload a new file or select an existing file already attached to this source.
                      </p>

                      {/* File mode toggle buttons */}
                      <div className="flex gap-2 mt-1">
                        <Button
                          type="button"
                          variant={fileMode === "upload" ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setFileMode("upload")
                            setSelectedExistingFileId(null)
                          }}
                        >
                          <Upload className="size-3.5 mr-1" />
                          Upload New
                        </Button>
                        <Button
                          type="button"
                          variant={fileMode === "existing" ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setFileMode("existing")
                            clearFiles()
                          }}
                          disabled={sourceFilesLoading}
                        >
                          <FileText className="size-3.5 mr-1" />
                          Select Existing
                          {sourceFilesLoading && (
                            <Loader2 className="size-3 ml-1 animate-spin" />
                          )}
                        </Button>
                      </div>

                      {/* Upload new file section */}
                      {fileMode === "upload" && (
                        <div className="space-y-2 mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={openFileDialog}
                            className="gap-2"
                          >
                            <Upload className="size-4" />
                            Choose File
                          </Button>
                          <input {...getInputProps()} className="sr-only" />
                          {files.length > 0 && (
                            <ul className="space-y-1">
                              {files.map(file => (
                                <li key={file.id} className="flex items-center gap-2 text-sm">
                                  <span className="wrap-break-word max-w-[435px]">
                                    {file.file.name}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="size-5 p-0"
                                    onClick={() => removeFile(file.id)}
                                    aria-label={`Remove ${file.file.name}`}
                                  >
                                    <X className="size-3" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Select existing file section */}
                      {fileMode === "existing" && (
                        <div className="mt-2 space-y-2">
                          {sourceFilesLoading && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="size-3.5 animate-spin" />
                              Loading source files…
                            </div>
                          )}

                          {sourceFilesError && !sourceFilesLoading && (
                            <p className="text-xs text-muted-foreground">
                              {sourceFilesError}
                            </p>
                          )}

                          {!sourceFilesLoading && sourceFiles.length > 0 && (
                            <div
                              className="flex flex-wrap gap-2 max-h-60 overflow-y-auto border rounded-md p-2"
                              role="listbox"
                              aria-label="Existing source files"
                            >
                              {sourceFiles.map((driveFile: GoogleDriveFile) => {
                                const isSelected = selectedExistingFileId === driveFile.id
                                return (
                                  <SourceFileCard
                                    key={driveFile.id}
                                    file={driveFile}
                                    isSelected={isSelected}
                                    onSelect={() => setSelectedExistingFileId(driveFile.id)}
                                  />
                                )
                              })}
                            </div>
                          )}

                          {!sourceFilesLoading && sourceFiles.length === 0 && !sourceFilesError && existingSourceId.trim() && (
                            <p className="text-xs text-muted-foreground">
                              Enter a valid source ID above to load its files.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* New source — title, file, and optional metadata */}
                {biblioMode === "new" && (
                  <div className="grid gap-4 mt-3">
                    {/* Title — required */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-title" className="text-sm font-medium">
                        Title *
                      </label>
                      <Input
                        id="report-title"
                        placeholder="e.g. National Malaria Survey Report 2023"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                      />
                    </div>

                    {/* File Upload — required */}
                    <div className="grid gap-1.5">
                      <label htmlFor="new-source-file-upload" className="text-sm font-medium">
                        Report File *
                      </label>
                      <div className="space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={openFileDialog}
                          className="gap-2"
                        >
                          <Upload className="size-4" />
                          Choose File
                        </Button>
                        <input {...getInputProps()} className="sr-only" />
                        {files.length > 0 && (
                          <ul className="space-y-1">
                            {files.map(file => (
                              <li key={file.id} className="flex items-center gap-2 text-sm">
                                <span className="wrap-break-word max-w-[435px]">
                                  {file.file.name}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="size-5 p-0"
                                  onClick={() => removeFile(file.id)}
                                  aria-label={`Remove ${file.file.name}`}
                                >
                                  <X className="size-3" />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {/* Publication Year */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-publication-year" className="text-sm font-medium">
                        Publication Year
                      </label>
                      <Input
                        id="report-publication-year"
                        type="number"
                        placeholder="e.g. 2023"
                        value={publicationYear}
                        onChange={e => setPublicationYear(e.target.value)}
                      />
                    </div>

                    {/* Authors */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-authors" className="text-sm font-medium">
                        Authors
                      </label>
                      <Input
                        id="report-authors"
                        placeholder="e.g. Smith, John; Doe, Jane"
                        value={authors}
                        onChange={e => setAuthors(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Enter authors in "Last, First" format, separated by semicolons.
                      </p>
                    </div>

                    {/* Institutions */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-institutions" className="text-sm font-medium">
                        Institutions
                      </label>
                      <Input
                        id="report-institutions"
                        placeholder="Comma-separated, e.g. WHO, Ministry of Health"
                        value={institutions}
                        onChange={e => setInstitutions(e.target.value)}
                      />
                    </div>

                    {/* City */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-city" className="text-sm font-medium">
                        City
                      </label>
                      <Input
                        id="report-city"
                        placeholder="e.g. Geneva"
                        value={city}
                        onChange={e => setCity(e.target.value)}
                      />
                    </div>

                    {/* Report Number */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-number" className="text-sm font-medium">
                        Report Number
                      </label>
                      <Input
                        id="report-number"
                        placeholder="e.g. WHO/HTM/GMP/2023.01"
                        value={reportNumber}
                        onChange={e => setReportNumber(e.target.value)}
                      />
                    </div>

                    {/* Pages */}
                    <div className="grid gap-1.5">
                      <label htmlFor="report-pages" className="text-sm font-medium">
                        Pages
                      </label>
                      <Input
                        id="report-pages"
                        placeholder="e.g. 1-120"
                        value={pages}
                        onChange={e => setPages(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Error display */}
        {submitError && (
          <p className="text-sm text-destructive" role="alert">
            {submitError}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm()
              clearFiles()
              setOpen(false)
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Adding Report…" : "Add Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
